/**
 * Labstep
 *
 * @module grid/NewIndex/components/ColumnHeader/ContextMenu
 * @desc Entity DataGrid Context Menu
 */

import type { FilterMetadataType } from 'labstep-web/components/Filter/Metadata/Form/types';
import { FilterPostNewIndexBoolean } from 'labstep-web/components/FilterPost/NewIndex/Boolean';
import { FilterPostNewIndexDate } from 'labstep-web/components/FilterPost/NewIndex/Date';
import { FilterPostNewIndexEntity } from 'labstep-web/components/FilterPost/NewIndex/Entity';
import { FilterPostNewIndexEntityState } from 'labstep-web/components/FilterPost/NewIndex/EntityState';
import { FilterPostNewIndexMetadata } from 'labstep-web/components/FilterPost/NewIndex/Metadata';
import { FilterPostNewIndexNumeric } from 'labstep-web/components/FilterPost/NewIndex/Numeric';
import { FilterPostNewIndexOptions } from 'labstep-web/components/FilterPost/NewIndex/Options';
import { FilterPostNewIndexResourceLocation } from 'labstep-web/components/FilterPost/NewIndex/ResourceLocation';
import { FilterPostNewIndexStatusType } from 'labstep-web/components/FilterPost/NewIndex/StatusType';
import { FilterPostNewIndexText } from 'labstep-web/components/FilterPost/NewIndex/Text';
import { useGridNewIndexServiceFromContext } from 'labstep-web/contexts/grid/hook';
import { useParamsContext } from 'labstep-web/contexts/params/hook';
import { Dropdown } from 'labstep-web/core/Dropdown';
import { DropdownPortal } from 'labstep-web/core/Dropdown/Portal';
import { Icon } from 'labstep-web/core/Icon';
import type { NewIndexColDef } from 'labstep-web/grid/NewIndex/coldefs/types';
import { GridNewIndexService } from 'labstep-web/grid/NewIndex/services/grid-new-index.service';
import { PostFilterFieldTypes } from 'labstep-web/grid/services/grid-filter.service';
import { MetadataTypeValues } from 'labstep-web/models/metadata/types';
import {
  PostFilterComparison,
  PostFilterComparisonHumanReadable,
} from 'labstep-web/services/postFilter/constants';
import React from 'react';
import type { GridNewIndexColumnHeaderContextMenuProps } from './types';

export const GridNewIndexColumnHeaderContextMenu: React.FC<
  GridNewIndexColumnHeaderContextMenuProps
> = ({ columnApi, column, firstItems, lastItems, trigger }) => {
  const { addPostFilter, setParams } = useParamsContext();
  const gridNewIndexService = useGridNewIndexServiceFromContext();

  const dropdownItems: React.ReactNode[] = firstItems
    ? [
        <React.Fragment key="first-items">
          {firstItems}
        </React.Fragment>,
      ]
    : [];

  const afterDividerItems = [];

  const colDef = column.getColDef() as NewIndexColDef;

  dropdownItems.push(
    <Dropdown.Item
      key="sort-ascending"
      onClick={(): void => {
        if (
          colDef.headerComponentParams?.postFilterSettings &&
          !colDef.headerComponentParams?.postFilterSettings.path &&
          !colDef.headerComponentParams?.postFilterSettings
            .disableSort
        ) {
          setParams({
            sort: `${colDef.headerComponentParams?.postFilterSettings?.field}`,
          });
        }
        if (
          colDef.headerComponentParams?.postFilterSettings &&
          colDef.headerComponentParams?.postFilterSettings.sort
        ) {
          setParams({
            sort: `${colDef.headerComponentParams?.postFilterSettings.sort}`,
          });
        }
        if (gridNewIndexService) {
          gridNewIndexService.sortColumn(
            trigger.props.column.colId,
            'asc',
          );
        }
      }}
    >
      <Icon name="sort amount up" /> Sort Ascending
    </Dropdown.Item>,
  );
  dropdownItems.push(
    <Dropdown.Item
      key="sort-descending"
      onClick={(): void => {
        if (
          colDef.headerComponentParams?.postFilterSettings &&
          !colDef.headerComponentParams?.postFilterSettings.path &&
          !colDef.headerComponentParams?.postFilterSettings
            .disableSort
        ) {
          setParams({
            sort: `-${colDef.headerComponentParams?.postFilterSettings?.field}`,
          });
        }
        if (
          colDef.headerComponentParams?.postFilterSettings &&
          colDef.headerComponentParams?.postFilterSettings.sort
        ) {
          setParams({
            sort: `-${colDef.headerComponentParams?.postFilterSettings.sort}`,
          });
        }
        if (gridNewIndexService) {
          gridNewIndexService.sortColumn(
            trigger.props.column.colId,
            'desc',
          );
        }
      }}
    >
      <Icon name="sort amount down" /> Sort Descending
    </Dropdown.Item>,
  );
  if (
    colDef.headerComponentParams?.postFilterSettings?.fieldType ===
      PostFilterFieldTypes.entity &&
    colDef.headerName &&
    colDef.headerComponentParams?.postFilterSettings.field &&
    colDef.headerComponentParams?.postFilterSettings.entityName
  ) {
    dropdownItems.push(
      <FilterPostNewIndexEntity
        key={`filter-entity-${colDef.colId}`}
        label={colDef.headerName}
        path={colDef.headerComponentParams?.postFilterSettings.path}
        field={colDef.headerComponentParams?.postFilterSettings.field}
        addPostFilter={addPostFilter}
        entityName={
          colDef.headerComponentParams?.postFilterSettings.entityName
        }
        extraParams={
          colDef.headerComponentParams?.postFilterSettings.extraParams
        }
      />,
    );
  }
  if (
    colDef.headerComponentParams?.postFilterSettings?.fieldType ===
      PostFilterFieldTypes.numeric &&
    colDef.headerName &&
    colDef.headerComponentParams?.postFilterSettings.field
  ) {
    dropdownItems.push(
      <FilterPostNewIndexNumeric
        key="filter-numeric"
        label={colDef.headerName}
        path={colDef.headerComponentParams?.postFilterSettings.path}
        field={colDef.headerComponentParams?.postFilterSettings.field}
        addPostFilter={addPostFilter}
        defaultValues={{
          comparison: {
            label: PostFilterComparisonHumanReadable.eq,
            value: PostFilterComparison.eq,
          },
          values: 0,
        }}
        multiplicator={
          colDef.headerComponentParams?.postFilterSettings
            .multiplicator
        }
      />,
    );
  }
  if (
    colDef.headerComponentParams?.postFilterSettings?.fieldType ===
      PostFilterFieldTypes.text &&
    colDef.headerName &&
    colDef.headerComponentParams?.postFilterSettings.field
  ) {
    dropdownItems.push(
      <FilterPostNewIndexText
        key="filter-text"
        label={colDef.headerName}
        path={colDef.headerComponentParams?.postFilterSettings.path}
        field={colDef.headerComponentParams?.postFilterSettings.field}
        addPostFilter={addPostFilter}
        defaultValues={{
          comparison: {
            label: 'Exact match',
            value: PostFilterComparison.eq,
          },
          values: '',
        }}
      />,
    );
  }
  if (
    colDef.headerComponentParams?.postFilterSettings?.fieldType ===
      PostFilterFieldTypes.date &&
    colDef.headerName &&
    colDef.headerComponentParams?.postFilterSettings.field
  ) {
    dropdownItems.push(
      <FilterPostNewIndexDate
        key="filter-date"
        label={colDef.headerName}
        path={colDef.headerComponentParams?.postFilterSettings.path}
        field={colDef.headerComponentParams?.postFilterSettings.field}
        addPostFilter={addPostFilter}
        defaultValues={{
          comparison: {
            label: 'Before',
            value: PostFilterComparison.lt,
          },
        }}
        nullableField={
          colDef.headerComponentParams?.postFilterSettings
            .nullableField
        }
      />,
    );
  }
  if (
    colDef.headerComponentParams?.postFilterSettings?.fieldType ===
      PostFilterFieldTypes.options &&
    colDef.headerName &&
    colDef.headerComponentParams?.postFilterSettings.field
  ) {
    dropdownItems.push(
      <FilterPostNewIndexOptions
        key="filter-options"
        label={colDef.headerName}
        path={colDef.headerComponentParams?.postFilterSettings.path}
        field={colDef.headerComponentParams?.postFilterSettings.field}
        addPostFilter={addPostFilter}
        defaultValues={{
          values: '',
        }}
        entityName={
          colDef.headerComponentParams?.postFilterSettings
            .entityName || ''
        }
      />,
    );
  }

  if (
    colDef.headerComponentParams?.postFilterSettings?.fieldType ===
      PostFilterFieldTypes.boolean &&
    colDef.headerName &&
    colDef.headerComponentParams?.postFilterSettings.field
  ) {
    dropdownItems.push(
      <FilterPostNewIndexBoolean
        key="filter-boolean"
        label={colDef.headerName}
        path={colDef.headerComponentParams?.postFilterSettings.path}
        field={colDef.headerComponentParams?.postFilterSettings.field}
        addPostFilter={addPostFilter}
        defaultValues={{
          bool: {
            label: 'True',
            value: true,
          },
        }}
      />,
    );
  }

  if (
    colDef.headerComponentParams?.postFilterSettings?.fieldType ===
      PostFilterFieldTypes.custom &&
    colDef.headerName &&
    colDef.headerComponentParams?.postFilterSettings.field &&
    colDef.colId === 'resource_location'
  ) {
    dropdownItems.push(
      <FilterPostNewIndexResourceLocation
        key="filter-resource-location"
        addPostFilter={addPostFilter}
      />,
    );
  }
  if (
    colDef.headerComponentParams?.postFilterSettings?.fieldType ===
      PostFilterFieldTypes.metadata &&
    colDef.headerName &&
    colDef.headerComponentParams?.postFilterSettings.field &&
    colDef.colId
  ) {
    const parts = colDef.colId.split(':');
    if (parts.length >= 4) {
      const type = parts[2];
      const metadataLabel = parts[3];
      dropdownItems.push(
        <FilterPostNewIndexMetadata
          key={`filter-metadata-${colDef.colId}`}
          path={
            colDef.headerComponentParams?.postFilterSettings.path!
          }
          addPostFilter={addPostFilter}
          defaultValues={{
            label: metadataLabel,
            type: {
              value: type as FilterMetadataType,
              label: MetadataTypeValues[type as FilterMetadataType],
            },
          }}
        />,
      );
    }
  }

  if (
    colDef.headerComponentParams?.postFilterSettings?.field ===
    'entity_state'
  ) {
    dropdownItems.push(
      <FilterPostNewIndexEntityState
        key={`filter-metadata-${colDef.colId}`}
        addPostFilter={addPostFilter}
      />,
    );
  }

  if (GridNewIndexService.isHideable(column)) {
    afterDividerItems.push(
      <Dropdown.Item
        key="hide-column"
        onClick={(): void =>
          columnApi.setColumnVisible(column, false)
        }
      >
        <Icon name="eye slash" /> Hide from View
      </Dropdown.Item>,
    );
  }

  if (
    colDef.headerComponentParams?.postFilterSettings?.field ===
    'status_type'
  ) {
    dropdownItems.push(
      <FilterPostNewIndexStatusType
        key={`filter-metadata-${colDef.colId}`}
        addPostFilter={addPostFilter}
      />,
    );
  }

  if (lastItems) {
    afterDividerItems.push(
      <React.Fragment key="last-items">{lastItems}</React.Fragment>,
    );
  }
  if (dropdownItems.length && afterDividerItems.length) {
    dropdownItems.push(<Dropdown.Divider key="main-divider" />);
  }
  dropdownItems.push(...afterDividerItems);
  if (colDef.columnOptions) {
    dropdownItems.push(<Dropdown.Divider key="options-divider" />);
    dropdownItems.push(
      <React.Fragment key="column-options">
        {colDef.columnOptions}
      </React.Fragment>,
    );
  }
  return dropdownItems.length ? (
    <DropdownPortal trigger={trigger}>{dropdownItems}</DropdownPortal>
  ) : (
    trigger
  );
};
